import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';

import { environment } from '../../../../../environments/environment';
import { currentUser } from '../../../../core/auth';
import { AppState } from '../../../../core/reducers';
import { ContactModel } from '../../../../core/lists';
import { QueryParamsModel, QueryResultsModel } from '../../../../core/_base/crud';

import {
  Category,
  Document,
  AccessHistory,
  EntityType,
  SharingConfig,
  SharingData,
} from '../models';

@Injectable({ providedIn: 'root' })
export class DocumentService {
  private companyId;
  private currentUserType;
  private baseUrl = environment.CORE_BASE;

  constructor(private http: HttpClient, private store: Store<AppState>) {
    this.store.select(currentUser).subscribe((user) => {
      this.companyId = user.companyID;
      this.currentUserType = user.userType;
    });
  }

  private static getCategoryPageQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.searchTerm) {
      fromString += `&search_term=${filter.searchTerm}`;
    } else {
      if (filter.id) fromString += `&id_has=${filter.id}`;
      if (filter.name) fromString += `&name_has=${filter.name}`;
      if (filter.notificationLevel) fromString += `&notification_level=${filter.notificationLevel}`;
      if (filter.origin) fromString += `&created_by_super_admin=${filter.origin}`;
      if (filter.mobileVisible?.toString()) fromString += `&mobile_visible=${filter.mobileVisible}`;
      if (filter.entityTypes?.length)
        filter.entityTypes.forEach((type) => (fromString += `&entity_type=${type}`));
    }

    return fromString;
  }

  private static getDocumentPageQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.entitiesIds?.length)
      filter.entitiesIds.forEach((id) => (fromString += `&entity_id=${id}`));

    if (filter.searchTerm) {
      fromString += `&search_term=${filter.searchTerm}`;
    } else {
      if (filter.id) fromString += `&id_has=${filter.id}`;
      if (filter.name) fromString += `&name_has=${filter.name}`;
      if (filter.description) fromString += `&description_has=${filter.description}`;
      if (filter.categoryId) fromString += `&category_id=${filter.categoryId}`;
      if (filter.types?.length) filter.types.forEach((type) => (fromString += `&type=${type}`));
      if (filter.created)
        fromString += `&upload_date=${filter.created}&user_timezone=${
          Intl.DateTimeFormat().resolvedOptions().timeZone
        }`;
      if (filter.requiresReview?.toString())
        fromString += `&requires_review=${filter.requiresReview}`;
      if (filter.reviewed?.toString()) fromString += `&reviewed=${filter.reviewed}`;
    }

    return fromString;
  }

  private static getAccessHistoryPageQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.documentId) fromString += `&document_id=${filter.documentId}`;

    if (filter.searchTerm) {
      fromString += `&search_term=${filter.searchTerm}`;
    } else {
      if (filter.id) fromString += `&id_has=${filter.id}`;
      if (filter.categoryId) fromString += `&document_category_id=${filter.categoryId}`;
      if (filter.entityId) fromString += `&entity_id=${filter.entityId}`;
      if (filter.accessType) fromString += `&interaction_type=${filter.accessType}`;
      if (filter.accessTime)
        fromString += `&access_date=${filter.accessTime}&user_timezone=${
          Intl.DateTimeFormat().resolvedOptions().timeZone
        }`;
      if (filter.userType) fromString += `&user_type=${filter.userType}`;
      if (filter.accessedBy) fromString += `&accessed_by_has=${filter.accessedBy}`;
    }

    return fromString;
  }

  getCategoryPage(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const params = new HttpParams({
      fromString: DocumentService.getCategoryPageQueryString(queryParams),
    });

    return this.http.get<QueryResultsModel>(
      `${this.baseUrl}/companies/${
        this.currentUserType === 'Super Admin' ? '0' : this.companyId
      }/documents/categories`,
      { params },
    );
  }

  findCategories(
    filter: { [key: string]: any },
    searchTerm: string,
  ): Observable<QueryResultsModel> {
    const queryParams = new QueryParamsModel({ ...filter, searchTerm }, 'asc', 'name', 0, 25);

    return this.getCategoryPage(queryParams);
  }

  findAllCategories(): Observable<any> {
    const queryParams = new QueryParamsModel({}, 'asc', 'name', 0, 5000);
    return this.getCategoryPage(queryParams);
  }

  getCategoryById(categoryId: number, isSuperAdmin?: boolean): Observable<{ data: Category }> {
    return this.http.get<{ data: Category }>(
      `${this.baseUrl}/companies/${
        isSuperAdmin ? 0 : this.companyId
      }/documents/categories/${categoryId}`,
    );
  }

  createCategory(category: Category): Observable<HttpResponse<{ data: Category }>> {
    return this.http.post<{ data: Category }>(
      `${this.baseUrl}/companies/${
        this.currentUserType === 'Super Admin' ? '0' : this.companyId
      }/documents/categories`,
      category,
      { observe: 'response' },
    );
  }

  updateCategory(category: Category): Observable<HttpResponse<{ data: Category }>> {
    return this.http.put<{ data: Category }>(
      `${this.baseUrl}/companies/${
        this.currentUserType === 'Super Admin' ? '0' : this.companyId
      }/documents/categories/${category.id}`,
      category,
      {
        observe: 'response',
      },
    );
  }

  getDocumentPage(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const params = new HttpParams({
      fromString: DocumentService.getDocumentPageQueryString(queryParams),
    });

    return this.http.get<QueryResultsModel>(
      `${this.baseUrl}/companies/${this.companyId}/documents`,
      { params },
    );
  }

  findDocuments(filter, searchTerm: string): Observable<QueryResultsModel> {
    const queryParams = new QueryParamsModel({ ...filter, searchTerm }, 'asc', 'name', 0, 25);

    return this.getDocumentPage(queryParams);
  }

  createDocument(document: Document): Observable<HttpResponse<{ data: Document }>> {
    return this.http.post<{ data: Document }>(
      `${this.baseUrl}/companies/${this.companyId}/documents`,
      document,
      { observe: 'response' },
    );
  }

  updateDocument(document: Document): Observable<HttpResponse<{ data: Document }>> {
    return this.http.put<{ data: Document }>(
      `${this.baseUrl}/companies/${this.companyId}/documents/${document.id}`,
      document,
      { observe: 'response' },
    );
  }

  getEntityTypes(): Observable<{ data: EntityType[] }> {
    return this.http.get<{ data: EntityType[] }>(`${this.baseUrl}/entity_types`);
  }

  getHtmlContent(url: string): Observable<any> {
    return this.http.get(url, { responseType: 'text' });
  }

  openDocumentSession(
    documentId: number,
    accessHistory: Partial<AccessHistory>,
  ): Observable<{ data: AccessHistory }> {
    return this.http.post<{ data: AccessHistory }>(
      `${this.baseUrl}/companies/${this.companyId}/documents/${documentId}/sessions`,
      accessHistory,
    );
  }

  closeDocumentSession(documentId: number, sessionId: number): Observable<{ data: AccessHistory }> {
    return this.http.put<{ data: AccessHistory }>(
      `${this.baseUrl}/companies/${this.companyId}/documents/${documentId}/sessions/${sessionId}`,
      {},
    );
  }

  getAccessHistoryPage(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const params = new HttpParams({
      fromString: DocumentService.getAccessHistoryPageQueryString(queryParams),
    });

    return this.http.get<QueryResultsModel>(
      `${this.baseUrl}/companies/${this.companyId}/documents/sessions`,
      { params },
    );
  }

  getSharingConfigs(): Observable<{ data: SharingConfig[] }> {
    return this.http.get<{ data: SharingConfig[] }>(
      `${this.baseUrl}/companies/${this.companyId}/documents/sharing-configs`,
    );
  }

  updateSharingConfigs(configs: SharingConfig[]): Observable<HttpResponse<void>> {
    return this.http.put<void>(
      `${this.baseUrl}/companies/${this.companyId}/documents/sharing-configs`,
      { configs },
      { observe: 'response' },
    );
  }

  findSharingReceivers(filter, searchTerm: string): Observable<{ data: ContactModel[] }> {
    let fromString = '';

    if (filter.documentId) fromString += `&document_id=${filter.documentId}`;
    if (filter.hasEmail) fromString += `&has_email=${filter.hasEmail}`;
    if (filter.hasFax) fromString += `&has_fax=${filter.hasFax}`;
    if (searchTerm) fromString += `&term=${searchTerm}`;

    return this.http.get<{ data: ContactModel[] }>(
      `${this.baseUrl}/companies/${this.companyId}/documents/share-receivers-autocomplete`,
      {
        params: new HttpParams({ fromString }),
      },
    );
  }

  shareDocument(data: SharingData): Observable<HttpResponse<void>> {
    return this.http.post<void>(
      `${this.baseUrl}/companies/${this.companyId}/documents/share`,
      data,
      { observe: 'response' },
    );
  }
}
